import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createCharacter, getStarterCharacterTypes, CharacterTypePublic } from '../api/characters';
import '../styles/global.css';

const CreateCharacter: React.FC = () => {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [characterTypes, setCharacterTypes] = useState<CharacterTypePublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const types = await getStarterCharacterTypes();
        setCharacterTypes(types);
        setLoading(false);
      } catch (err) {
        setError('Failed to load character types');
        setLoading(false);
      }
    };
    fetchTypes();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedName = name.trim();

    if (trimmedName.length < 1 || trimmedName.length > 20) {
      setError('Character name must be 1-20 characters');
      return;
    }

    if (!selectedTypeId) {
      setError('Please select a character type');
      return;
    }

    setIsSubmitting(true);
    try {
      await createCharacter(trimmedName, selectedTypeId);
      await refreshUser();
      navigate('/account');
    } catch (err: any) {
      const message = err.response?.data?.detail || 'Failed to create character';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <p>Loading character types...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <h1 className="title">Create Your Character</h1>
        <p className="subtitle">Choose your character type and give them a name</p>

        <form onSubmit={handleSubmit} className="form">
          <div className="avatar-selection">
            <label>Choose Your Character</label>
            <div className="avatar-grid">
              {characterTypes.map((charType) => (
                <button
                  key={charType.id}
                  type="button"
                  className={`avatar-option ${selectedTypeId === charType.id ? 'selected' : ''}`}
                  onClick={() => setSelectedTypeId(charType.id)}
                  disabled={isSubmitting}
                  title={charType.description || charType.name}
                >
                  <img src={charType.avatar_url} alt={charType.name} />
                  <span className="avatar-name">{charType.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="characterName">
              Character Name ({name.trim().length}/20)
            </label>
            <input
              type="text"
              id="characterName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter character name"
              maxLength={20}
              disabled={isSubmitting}
              required
            />
          </div>

          {error && <div className="error">{error}</div>}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting || !name.trim() || !selectedTypeId}
          >
            {isSubmitting ? 'Creating...' : 'Create Character'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateCharacter;
